import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export type OperationType = 'jsonata' | 'ai';
export type OperationScope = 'field' | 'document';

export interface ProcessingOperation {
  id: string;
  description: string;
  type: OperationType;
  scope?: OperationScope;
  target?: string;
  expression?: string;
  instruction?: string;
  params?: Record<string, any>;
  paramsError?: boolean;
}

export interface CatalogOperation {
  id: string;
  title: string;
  summary: string;
  type: 'jsonata' | 'function' | 'convention' | 'stage' | 'pending';
  when?: string;
  expression?: string;
  signature?: string;
  caveats?: string[];
  variants?: { name: string; expression: string }[];
  verification?: { input: any; output: any }[];
  origin?: string;
}

export interface CatalogConcept {
  id: string;
  title: string;
  summary: string;
  explanation: string;
  was: string;
}

export interface Catalog {
  version: string;
  note?: string;
  criteria: string;
  language?: string;
  languages?: string[];
  concepts: CatalogConcept[];
  mapping: { id: string; concept: string; was: string }[];
  operations: CatalogOperation[];
  summary: string;
}

export interface LabelSuggestion {
  id: string;
  summary: string;
  type: CatalogOperation['type'];
  reason: string;
  expression?: string;
}

export interface InspectedLabel {
  label: string;
  occurrences: number;
  examples: string[];
  suggestions: LabelSuggestion[];
}

export interface Inspection {
  shape: 'extraction' | 'relation' | null;
  labels: InspectedLabel[];
  error?: string;
}

export interface Processing {
  id: string;
  name: string;
  input_archive_ids: string[];
  operations: ProcessingOperation[];
  delfos_server_config_id?: string | null;
  llm_model?: string | null;
  model_id?: string | null;
  created_at?: string;
  updated_at?: string;
  tag?: {
    id: string;
    name: string;
    text_color: string;
    bg_color: string;
  } | null;
}

export interface ProcessingExecution {
  id: string;
  processing_id?: string;
  result?: any;
  config?: {
    inputs?: string[];
    input_data?: Record<string, any>;
    operations?: ProcessingOperation[];
    llm_model?: string | null;
    ai_model_language?: string;
    steps?: Record<string, any>;
    [key: string]: any;
  } | null;
  tokens?: any;
  user?: string;
  created_at: string;
}

export interface AssistSuggestion {
  description: string;
  type: OperationType;
  expression?: string;
  instruction?: string;
}

export interface AssistPayload {
  text: string;
  sample?: any;
  delfos_server_config_id?: string | null;
  llm_model?: string | null;
}

export interface AiOperationPayload {
  input: any;
  instruction: string;
  delfos_server_config_id?: string | null;
  llm_model?: string | null;
}

export interface RunPipelinePayload {
  input: any;
  operations: ProcessingOperation[];
  delfos_server_config_id?: string | null;
  llm_model?: string | null;
}

export interface RunPipelineStepMetadata {
  description?: string | null;
  type: OperationType;
  target?: string;
  status: string;
  duration_ms: number;
  error?: string | null;
}

export interface TestOperationPayload {
  input: any;
  operation: ProcessingOperation;
  preceding?: ProcessingOperation[];
  delfos_server_config_id?: string | null;
  llm_model?: string | null;
}

export interface TestOperationResult {
  output: any;
  error?: string | null;
  duration_ms?: number;
}

export interface RunPipelineResult {
  result: any;
  metadata?: { steps: RunPipelineStepMetadata[] };
}

export interface SaveExecutionPayload {
  result: any;
  config?: any;
  tokens?: any;
}

@Injectable({ providedIn: 'root' })
export class ProcessingsService {
  private baseUrl = '/api/processings/';

  constructor(private http: HttpClient) {}

  getById(processingId: string): Observable<Processing> {
    return this.http
      .get<{ message: string; processing: Processing }>(`${this.baseUrl}${processingId}`)
      .pipe(map(res => res.processing));
  }

  list(): Observable<{ id: string; name: string; updated_at?: string }[]> {
    return this.http
      .get<{ message: string; processings: { id: string; name: string; updated_at?: string }[] }>(this.baseUrl)
      .pipe(map(res => res.processings || []));
  }

  create(processing: Omit<Processing, 'id' | 'created_at' | 'updated_at'>): Observable<string> {
    return this.http
      .post<{ message: string; processing_id: string }>(this.baseUrl, processing)
      .pipe(map(res => res.processing_id));
  }

  update(processingId: string, processing: Partial<Processing>): Observable<string> {
    return this.http.put(`${this.baseUrl}${processingId}`, processing, { responseType: 'text' });
  }

  rename(processingId: string, name: string): Observable<string> {
    return this.http.put(`${this.baseUrl}${processingId}`, { name }, { responseType: 'text' }) as Observable<string>;
  }

  delete(processingId: string): Observable<string> {
    return this.http.delete(`${this.baseUrl}${processingId}`, { responseType: 'text' });
  }

  saveExecution(processingId: string, payload: SaveExecutionPayload): Observable<ProcessingExecution> {
    return this.http
      .post<{ message: string; execution: ProcessingExecution }>(`${this.baseUrl}${processingId}/executions`, payload)
      .pipe(map(res => res.execution));
  }

  updateExecution(processingId: string, executionId: string, payload: SaveExecutionPayload): Observable<ProcessingExecution> {
    return this.http
      .put<{ message: string; execution: ProcessingExecution }>(`${this.baseUrl}${processingId}/executions/${executionId}`, payload)
      .pipe(map(res => res.execution));
  }

  getExecutions(processingId: string): Observable<ProcessingExecution[]> {
    return this.http
      .get<{ message: string; executions: ProcessingExecution[] }>(`${this.baseUrl}${processingId}/executions`)
      .pipe(map(res => res.executions));
  }

  getExecution(processingId: string, executionId: string): Observable<ProcessingExecution> {
    return this.http
      .get<{ message: string; execution: ProcessingExecution }>(`${this.baseUrl}${processingId}/executions/${executionId}`)
      .pipe(map(res => res.execution));
  }

  assist(payload: AssistPayload): Observable<AssistSuggestion[]> {
    return this.http
      .post<{ message: string; suggestions: any }>(`${this.baseUrl}assist`, payload)
      .pipe(map(res => (Array.isArray(res.suggestions) ? res.suggestions : (res.suggestions?.suggestions || []))));
  }

  aiOperation(payload: AiOperationPayload): Observable<any> {
    return this.http
      .post<{ message: string; result: any }>(`${this.baseUrl}ai-operation`, payload)
      .pipe(map(res => (res.result?.result !== undefined ? res.result.result : res.result)));
  }

  testOperation(payload: TestOperationPayload): Observable<TestOperationResult> {
    return this.http
      .post<{ message: string; result: TestOperationResult }>(`${this.baseUrl}test-operation`, payload)
      .pipe(map(res => res.result));
  }

  catalog(): Observable<Catalog> {
    return this.http.get<Catalog>(`${this.baseUrl}catalog`);
  }

  inspect(input: any): Observable<Inspection> {
    return this.http.post<Inspection>(`${this.baseUrl}inspect`, { input });
  }

  run(payload: RunPipelinePayload): Observable<RunPipelineResult> {
    return this.http
      .post<{ message: string; result: RunPipelineResult }>(`${this.baseUrl}run`, payload)
      .pipe(map(res => res.result));
  }
}
